// PaperMapsMarquee — auto-scrolling ticker of Shop product visuals for
// the homepage "GEOVINUM MAPS · LE SOCLE" section.
//
// Fetches the first ~24 published Shop products and pipes their `/api/shop/preview/{pid}`
// URL through an infinite horizontal scroll. Each tile links to the PDP.
// Two rows scrolling in opposite directions give the section movement
// without feeling like a slideshow.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

const Row = ({ items, reverse = false, speed = 60 }) => {
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(speed);

  // Duplicate the item list so the CSS translate loop is seamless.
  const doubled = useMemo(() => [...items, ...items], [items]);

  useEffect(() => {
    // Adapt duration to width — longer rows scroll slower so pixel-speed stays constant.
    const track = trackRef.current;
    if (!track) return;
    const w = track.scrollWidth / 2;   // one loop width (half because we duplicated)
    const s = 90;                       // px/s target
    setDuration(Math.max(30, Math.round(w / s)));
  }, [doubled.length]);

  return (
    <div className="relative overflow-hidden" data-testid={reverse ? "papermaps-row-reverse" : "papermaps-row-forward"}>
      <div
        ref={trackRef}
        className="flex gap-6 whitespace-nowrap will-change-transform"
        style={{
          animation: `pm-scroll-${reverse ? "rev" : "fwd"} ${duration}s linear infinite`,
        }}
      >
        {doubled.map((it, idx) => (
          <Link
            key={`${it.product_id}-${idx}`}
            to={`/shop/${it.product_id}`}
            className="shrink-0 group"
            aria-label={it.title}
          >
            <div className="w-[220px] h-[300px] rounded-[6px] overflow-hidden bg-white border border-[color:var(--gv-line)] shadow-[0_18px_40px_-20px_rgba(46,80,103,0.35)] transition-transform duration-500 group-hover:-translate-y-1">
              <img
                src={`${API}/api/shop/preview/${it.product_id}`}
                alt={it.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const PaperMapsMarquee = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    apiFetch("/api/shop/products?limit=24").then(({ ok, data }) => {
      if (!ok) return;
      const list = (data?.items || []).filter((p) => p.product_id);
      // Shuffle for variety — seeded by date so it stays stable during a session.
      const seed = new Date().toISOString().slice(0, 10);
      const rnd = (i) => {
        let h = 0; for (const c of seed) h = (h << 5) - h + c.charCodeAt(0);
        return Math.sin(h + i) * 10000 % 1;
      };
      list.sort((a, b) => rnd(a.product_id.charCodeAt(0)) - rnd(b.product_id.charCodeAt(0)));
      setItems(list.slice(0, 16));
    });
  }, []);

  if (items.length < 4) return null;
  const half = Math.ceil(items.length / 2);
  const row1 = items.slice(0, half);
  const row2 = items.slice(half);

  return (
    <div className="space-y-5" data-testid="papermaps-marquee">
      <style>{`
        @keyframes pm-scroll-fwd { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes pm-scroll-rev { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>
      <Row items={row1} speed={60} />
      <Row items={row2} reverse speed={80} />
    </div>
  );
};

export default PaperMapsMarquee;
